import { formatCurrency } from './formatting';
import { getBrazilCivilDate } from './competence';

export const PAYMENTS_RECEIVED_PERIODS = [
  'today',
  'yesterday',
  'last7',
  'custom',
] as const;

export type PaymentsReceivedPeriod = (typeof PAYMENTS_RECEIVED_PERIODS)[number];

const MAX_RANGE_DAYS = 366;

export interface PaymentsReceivedQuery {
  dateStart: string;
  dateEnd: string;
  searchName: string;
  paymentGateway: string[];
  page: number;
  pageSize: number;
}

export type PaymentsReceivedQueryResult =
  | { ok: true; query: PaymentsReceivedQuery }
  | { ok: false; message: string };

export interface PaymentsReceivedRow {
  paymentHistoryId: number;
  studentId: number;
  studentName: string;
  studentEmail: string;
  payerName: string | null;
  planName: string | null;
  paidAt: string;
  value: number | null;
  currency: string | null;
  paymentSystem: string | null;
}

export interface PaymentsReceivedDashboard {
  dateStart: string;
  dateEnd: string;
  page: number;
  pageSize: number;
  totalCount: number;
  payments: PaymentsReceivedRow[];
}

export function resolvePaymentsReceivedQuery(
  period: PaymentsReceivedPeriod,
  customStart: string,
  customEnd: string,
  searchName: string,
  paymentGateway: readonly string[],
  page: number,
  pageSize: number,
  reference: Date = new Date(),
): PaymentsReceivedQueryResult {
  const range = period === 'custom'
    ? resolveCustomRange(customStart, customEnd)
    : resolvePresetRange(period, reference);

  if (!range.ok) {
    return range;
  }

  return {
    ok: true,
    query: {
      dateStart: range.query.dateStart,
      dateEnd: range.query.dateEnd,
      searchName,
      paymentGateway: [...paymentGateway],
      page,
      pageSize,
    },
  };
}

export function formatPaymentAmount(value: number | null, currency: string | null): string {
  if (value == null) {
    return '—';
  }

  if (currency === 'BRL' || currency === 'USD') {
    return formatCurrency(value, currency);
  }

  const formatted = value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${formatted}` : formatted;
}

export function parsePaymentsReceivedDashboard(value: unknown): PaymentsReceivedDashboard | null {
  if (!isRecord(value)) {
    return null;
  }

  const dateStart = readIsoDate(value.dateStart);
  const dateEnd = readIsoDate(value.dateEnd);
  const payments = readPayments(value.payments);
  const totalCount = readNonNegativeInteger(value.totalCount);
  const page = readPositiveInteger(value.page) ?? 1;
  const pageSize = readPositiveInteger(value.pageSize) ?? 20;
  if (!dateStart || !dateEnd || !payments || totalCount == null) {
    return null;
  }

  return {
    dateStart,
    dateEnd,
    page,
    pageSize,
    totalCount,
    payments,
  };
}

function resolvePresetRange(
  period: Exclude<PaymentsReceivedPeriod, 'custom'>,
  reference: Date,
): PaymentsReceivedQueryResult {
  const today = getBrazilCivilDate(reference);
  if (period === 'today') {
    return okRange(today, today);
  }

  if (period === 'yesterday') {
    const yesterday = getBrazilCivilDate(reference, -1);
    return okRange(yesterday, yesterday);
  }

  return okRange(getBrazilCivilDate(reference, -6), today);
}

function okRange(dateStart: string, dateEnd: string): PaymentsReceivedQueryResult {
  return { ok: true, query: {
    dateStart,
    dateEnd,
    searchName: '',
    paymentGateway: [],
    page: 1,
    pageSize: 20,
  } };
}

function resolveCustomRange(customStart: string, customEnd: string): PaymentsReceivedQueryResult {
  if (!isIsoDate(customStart) || !isIsoDate(customEnd)) {
    return { ok: false, message: 'Informe a data inicial e a data final.' };
  }

  if (customStart > customEnd) {
    return { ok: false, message: 'A data inicial não pode ser maior que a data final.' };
  }

  const start = Date.parse(`${customStart}T00:00:00Z`);
  const end = Date.parse(`${customEnd}T00:00:00Z`);
  const days = Math.round((end - start) / 86_400_000);
  if (days > MAX_RANGE_DAYS) {
    return { ok: false, message: 'O período pode ter no máximo 366 dias.' };
  }

  return okRange(customStart, customEnd);
}

function readPayments(value: unknown): PaymentsReceivedRow[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const payments: PaymentsReceivedRow[] = [];
  for (const item of value) {
    const row = readPayment(item);
    if (!row) {
      return null;
    }
    payments.push(row);
  }

  return payments;
}

function readPayment(value: unknown): PaymentsReceivedRow | null {
  if (!isRecord(value)) {
    return null;
  }

  const paymentHistoryId = readPositiveInteger(value.paymentHistoryId);
  const studentId = readPositiveInteger(value.studentId);
  const studentName = readOptionalText(value.studentName);
  const studentEmail = value.studentEmail == null ? '' : readOptionalText(value.studentEmail);
  const payerName = value.payerName == null ? null : readOptionalText(value.payerName);
  const planName = value.planName == null ? null : readOptionalText(value.planName);
  const paidAt = readTimestamp(value.paidAt);
  const amount = value.value == null ? null : readNonNegativeAmount(value.value);
  const currency = value.currency == null ? null : readOptionalText(value.currency);
  const paymentSystem = value.paymentSystem == null ? null : readOptionalText(value.paymentSystem);

  if (
    paymentHistoryId == null
    || studentId == null
    || studentName == null
    || studentEmail == null
    || (value.payerName != null && payerName == null)
    || (value.planName != null && planName == null)
    || !paidAt
    || (value.value != null && amount == null)
    || (value.currency != null && currency == null)
    || (value.paymentSystem != null && paymentSystem == null)
  ) {
    return null;
  }

  return {
    paymentHistoryId,
    studentId,
    studentName,
    studentEmail,
    payerName,
    planName,
    paidAt,
    value: amount,
    currency,
    paymentSystem,
  };
}

function isIsoDate(value: string): boolean {
  return readIsoDate(value) != null;
}

function readIsoDate(value: unknown): string | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    return null;
  }

  return value;
}

function readTimestamp(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return value;
}

function readPositiveInteger(value: unknown): number | null {
  const parsed = readNumber(value);
  if (parsed == null || !Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function readNonNegativeInteger(value: unknown): number | null {
  const parsed = readNumber(value);
  if (parsed == null || !Number.isInteger(parsed) || parsed < 0) {
    return null;
  }

  return parsed;
}

function readNonNegativeAmount(value: unknown): number | null {
  const parsed = readNumber(value);
  if (parsed == null || parsed < 0) {
    return null;
  }

  return parsed;
}

function readNumber(value: unknown): number | null {
  const parsed = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== ''
      ? Number(value)
      : Number.NaN;

  if (!Number.isFinite(parsed) || parsed > Number.MAX_SAFE_INTEGER) {
    return null;
  }

  return parsed;
}

function readOptionalText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed === '' ? '' : trimmed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
